import { ApiSync } from "@incendium/api";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import WooCommercePluginInstructions from "features/dataConnectors/components/WooCommercePluginInstructions";
import {
  getWooCommerceConnectionStatus,
  parseWooStoreMeta,
} from "features/dataConnectors/utils/woocommerceStoreMeta";

interface IWooCommerceConnectionStatusProps {
  sync: ApiSync;
  onRefresh?: () => void;
  refreshing?: boolean;
}

function alertSeverity(
  phase: ReturnType<typeof getWooCommerceConnectionStatus>["phase"],
): "success" | "info" | "warning" | "error" {
  switch (phase) {
    case "ready":
      return "success";
    case "init_pending":
    case "settings_pending":
      return "info";
    case "oauth_error":
      return "error";
    default:
      return "warning";
  }
}

function WooCommerceConnectionStatus({
  sync,
  onRefresh,
  refreshing,
}: IWooCommerceConnectionStatusProps) {
  const status = getWooCommerceConnectionStatus(sync);
  const plugin = parseWooStoreMeta(sync.client?.userMeta)?.plugin;

  return (
    <Box mt={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="subtitle2">Connection status</Typography>
        {onRefresh && (
          <Button
            size="small"
            variant="text"
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? <CircularProgress size={16} /> : "Refresh status"}
          </Button>
        )}
      </Stack>
      <Alert severity={alertSeverity(status.phase)} sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{status.title}</Typography>
        {status.message && (
          <Typography variant="body2" mt={0.5}>
            {status.message}
          </Typography>
        )}
        {status.phase === "ready" && plugin?.wp_version && (
          <Typography variant="caption" display="block" mt={0.5}>
            WordPress {plugin.wp_version}
            {plugin.site_url ? ` · ${plugin.site_url}` : ""}
          </Typography>
        )}
      </Alert>
      {status.showInstallInstructions && <WooCommercePluginInstructions />}
    </Box>
  );
}

export default WooCommerceConnectionStatus;
